import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetAllProcesses, useGetAllUsers } from "@/hooks";
import {
  useDeleteProcessTrigger,
  useProcessTriggers,
} from "@/hooks/process-trigger/use-process-trigger";
import type { ProcessTrigger } from "@/types/process-trigger.types";
import type { TriggerStep } from "./condition-group-builder";
import TriggerEditorDialog from "./trigger-editor-dialog";

interface Props {
  processId: string;
  // The saved process (GET /processes/:id). Triggers validate against saved steps.
  processData: any;
}

const userLabel = (u: { firstName?: string | null; lastName?: string | null; email: string }) =>
  `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email;

const ProcessTriggersSection: React.FC<Props> = ({ processId, processData }) => {
  const { t } = useTranslation();
  const { data: triggers = [], isLoading } = useProcessTriggers(processId);
  const { data: processes } = useGetAllProcesses();
  const { data: users } = useGetAllUsers();
  const deleteMutation = useDeleteProcessTrigger(processId);
  const [editing, setEditing] = useState<ProcessTrigger | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const steps: TriggerStep[] = useMemo(
    () =>
      (processData?.forms ?? []).map((pf: any) => ({
        formId: pf.formId,
        name: pf.form?.name ?? "",
        order: pf.order,
        design: pf.form?.design,
      })),
    [processData]
  );
  const stepName = (formId: string | null) =>
    steps.find((s) => s.formId === formId)?.name ?? t("processTriggers.list.removedStep");

  const processOptions = useMemo(
    () =>
      (processes?.data ?? [])
        .filter((p: any) => p.status === "ENABLED" && !p.archived)
        .map((p: any) => ({ value: p.id, label: p.name, hint: p.group?.name })),
    [processes]
  );
  const userOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : users?.data ?? [])
        .filter((u: any) => u.status === "ENABLED")
        .map((u: any) => ({ value: u.id, label: userLabel(u), hint: u.email })),
    [users]
  );

  const openEditor = (trigger: ProcessTrigger | null) => {
    setEditing(trigger);
    setIsEditorOpen(true);
  };

  const handleDelete = (trigger: ProcessTrigger) => {
    if (!window.confirm(t("processTriggers.list.confirmDelete", { name: trigger.name }))) return;
    deleteMutation.mutate(trigger.id, {
      onSuccess: () => toast.success(t("processTriggers.list.deleted")),
      onError: () => toast.error(t("processTriggers.list.deleteFailed")),
    });
  };

  const describeRecipient = (trigger: ProcessTrigger) =>
    trigger.applicantSource === "FIXED_USER" && trigger.fixedUser
      ? userLabel(trigger.fixedUser)
      : t(`processTriggers.applicantSource.${trigger.applicantSource}`);

  return (
    <section className="mt-10 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-medium text-darkBlue">
            <Zap className="h-5 w-5" /> {t("processTriggers.list.title")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{t("processTriggers.list.description")}</p>
        </div>
        <button
          type="button"
          className="main-dark-button flex items-center gap-1 !px-4 shrink-0"
          disabled={steps.length === 0}
          onClick={() => openEditor(null)}
        >
          <Plus className="h-4 w-4" /> {t("processTriggers.list.add")}
        </button>
      </div>

      {steps.length === 0 && (
        <p className="mt-4 text-sm text-amber-700">{t("processTriggers.list.saveStepsFirst")}</p>
      )}

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <p className="text-sm text-gray-500">{t("processManagement.loading")}</p>
        ) : triggers.length === 0 ? (
          <p className="text-sm text-gray-500">{t("processTriggers.list.empty")}</p>
        ) : (
          triggers.map((trigger) => (
            <div
              key={trigger.id}
              className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-3"
            >
              <div className="space-y-1 text-sm min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{trigger.name}</span>
                  {!trigger.isActive && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {t("processTriggers.list.inactive")}
                    </span>
                  )}
                </div>
                <p className="text-gray-600">
                  {trigger.event === "STEP_COMPLETED"
                    ? t("processTriggers.list.whenStep", { step: stepName(trigger.sourceFormId) })
                    : t("processTriggers.list.whenProcess")}{" "}
                  → <span className="font-medium">{trigger.targetProcess.name}</span>
                </p>
                <p className="text-gray-500">
                  {t(`processTriggers.mode.${trigger.mode}`)}
                  {trigger.mode === "RULES" &&
                    ` (${t("processTriggers.list.ruleCount", {
                      count: trigger.conditions?.conditions.length ?? 0,
                    })})`}
                  {" · "}
                  {t("processTriggers.list.for")} {describeRecipient(trigger)}
                  {trigger.dueInDays
                    ? ` · ${t("processTriggers.list.dueIn", { count: trigger.dueInDays })}`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => openEditor(trigger)}
                  className="p-2 text-gray-500 hover:text-darkBlue"
                  aria-label={t("processTriggers.list.edit")}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(trigger)}
                  className="p-2 text-gray-500 hover:text-red-600"
                  aria-label={t("processTriggers.list.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <TriggerEditorDialog
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        processId={processId}
        steps={steps}
        trigger={editing}
        processOptions={processOptions}
        userOptions={userOptions}
      />
    </section>
  );
};

export default ProcessTriggersSection;
